import React, { useEffect, useState } from "react";
import { Button, BusyIndicator, Title } from "@ui5/webcomponents-react";
import { toast } from "sonner";
import { AdminApi } from "../../api/client";

const apiError = (e, fallback) => e?.response?.data?.message || fallback;
const WEEKDAYS = [["1", "Montag"], ["2", "Dienstag"], ["3", "Mittwoch"], ["4", "Donnerstag"], ["5", "Freitag"], ["6", "Samstag"], ["7", "Sonntag"]];
const weekdayName = (n) => (WEEKDAYS.find(([v]) => Number(v) === Number(n)) || [null, "?"])[1];
const deDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString("de-DE") : "");

export default function SchedulePage() {
  const [blackouts, setBlackouts] = useState(null);
  const [rules, setRules] = useState(null);
  const [busy, setBusy] = useState(false);

  // Blackout form
  const [boFrom, setBoFrom] = useState("");
  const [boTo, setBoTo] = useState("");
  const [boLabel, setBoLabel] = useState("");

  // Rule form
  const [rWeekday, setRWeekday] = useState(1);
  const [rStart, setRStart] = useState("09:00");
  const [rEnd, setREnd] = useState("12:00");
  const [rDur, setRDur] = useState(30);
  const [rollWeeks, setRollWeeks] = useState(8);

  const load = () => {
    AdminApi.listBlackouts().then(setBlackouts).catch(() => setBlackouts([]));
    AdminApi.listRules().then(setRules).catch(() => setRules([]));
  };
  useEffect(() => { load(); }, []);

  const addBlackout = async () => {
    if (!boFrom || !boTo) { toast.error("Bitte Von- und Bis-Datum wählen."); return; }
    setBusy(true);
    try {
      await AdminApi.createBlackout({ from: boFrom, to: boTo, label: boLabel.trim() || null });
      toast.success("Sperrzeit angelegt.");
      setBoFrom(""); setBoTo(""); setBoLabel(""); load();
    } catch (e) { toast.error(apiError(e, "Anlegen fehlgeschlagen.")); }
    finally { setBusy(false); }
  };
  const removeBlackout = async (id) => {
    if (!window.confirm("Diese Sperrzeit löschen?")) return;
    try { await AdminApi.deleteBlackout(id); toast.success("Sperrzeit gelöscht."); load(); }
    catch (e) { toast.error(apiError(e, "Löschen fehlgeschlagen.")); }
  };

  const addRule = async () => {
    if (rEnd <= rStart) { toast.error("Ende muss nach Start liegen."); return; }
    setBusy(true);
    try {
      await AdminApi.createRule({ weekday: Number(rWeekday), startTime: rStart, endTime: rEnd, durationMinutes: Number(rDur) });
      toast.success("Regel angelegt.");
      load();
    } catch (e) { toast.error(apiError(e, "Anlegen fehlgeschlagen.")); }
    finally { setBusy(false); }
  };
  const removeRule = async (id) => {
    if (!window.confirm("Diese Regel löschen?")) return;
    try { await AdminApi.deleteRule(id); toast.success("Regel gelöscht."); load(); }
    catch (e) { toast.error(apiError(e, "Löschen fehlgeschlagen.")); }
  };
  const roll = async () => {
    setBusy(true);
    try {
      const r = await AdminApi.rollSlots(Number(rollWeeks));
      toast.success(`${r.created} Slot(s) erzeugt (${r.skippedOverlap} vorhanden, ${r.skippedBlackout} Sperrzeit übersprungen).`);
    } catch (e) { toast.error(apiError(e, "Generierung fehlgeschlagen.")); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <Title level="H2" style={{ margin: "8px 0 16px" }}>Verfügbarkeit</Title>

      <div className="card">
        <h3>Sperrzeiten (Urlaub / Feiertage)</h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          In diesem Zeitraum werden keine Slots generiert und bestehende Slots werden ausgeblendet.
        </p>
        <div className="toolbar-row">
          <Field label="Von"><input className="native-input" type="date" value={boFrom} onChange={(e) => setBoFrom(e.target.value)} /></Field>
          <Field label="Bis"><input className="native-input" type="date" value={boTo} onChange={(e) => setBoTo(e.target.value)} /></Field>
          <Field label="Bezeichnung (optional)"><input className="native-input" value={boLabel} placeholder="z.B. Sommerurlaub" onChange={(e) => setBoLabel(e.target.value)} /></Field>
          <Button design="Emphasized" icon="add" disabled={busy} onClick={addBlackout}>Sperrzeit anlegen</Button>
        </div>
        {blackouts === null ? <BusyIndicator active /> : blackouts.length === 0 ? (
          <p className="muted">Keine Sperrzeiten.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Von</th><th>Bis</th><th>Bezeichnung</th><th></th></tr></thead>
              <tbody>
                {blackouts.map((b) => (
                  <tr key={b.id}>
                    <td>{deDate(b.from)}</td><td>{deDate(b.to)}</td><td>{b.label || "—"}</td>
                    <td style={{ textAlign: "right" }}><Button design="Transparent" icon="delete" onClick={() => removeBlackout(b.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h3>Wochen-Arbeitszeiten (automatische Slots)</h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          Aus diesen Regeln werden rollierend Slots für die nächsten Wochen erzeugt (täglich automatisch + per Knopf).
        </p>
        <div className="toolbar-row">
          <Field label="Wochentag">
            <select className="native-input" value={rWeekday} onChange={(e) => setRWeekday(e.target.value)}>
              {WEEKDAYS.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
          </Field>
          <Field label="Von"><input className="native-input" type="time" value={rStart} onChange={(e) => setRStart(e.target.value)} /></Field>
          <Field label="Bis"><input className="native-input" type="time" value={rEnd} onChange={(e) => setREnd(e.target.value)} /></Field>
          <Field label="Slot-Dauer (Min)"><input className="native-input" type="number" min="5" step="5" value={rDur} style={{ width: 90 }} onChange={(e) => setRDur(e.target.value)} /></Field>
          <Button design="Emphasized" icon="add" disabled={busy} onClick={addRule}>Regel anlegen</Button>
        </div>
        {rules === null ? <BusyIndicator active /> : rules.length === 0 ? (
          <p className="muted">Noch keine Regeln.</p>
        ) : (
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>Wochentag</th><th>Von</th><th>Bis</th><th>Dauer</th><th></th></tr></thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td>{weekdayName(r.weekday)}</td><td>{r.startTime}</td><td>{r.endTime}</td><td>{r.durationMinutes} Min</td>
                    <td style={{ textAlign: "right" }}><Button design="Transparent" icon="delete" onClick={() => removeRule(r.id)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#4a5867" }}>Jetzt generieren für</span>
          <input className="native-input" type="number" min="1" max="52" value={rollWeeks} style={{ width: 70 }} onChange={(e) => setRollWeeks(e.target.value)} />
          <span style={{ fontSize: 13, color: "#4a5867" }}>Wochen</span>
          <Button design="Transparent" icon="generate-shortcut" disabled={busy || !(rules && rules.length)} onClick={roll}>Slots jetzt erzeugen</Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="field-col">
      <label style={{ fontSize: 12, color: "#4a5867", fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
